import { useEffect, useRef, useState } from "react";
import { createRoomInviteBridge } from "./RoomInviteBridge";
import { isRoomInviteRegisteredPlayer, roomInviteGamePath, roomInviteHomePath,
  roomInviteRegistrationPath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

export function useRoomInvite({ location, history, getPersist, setPersist, getGameUrl, hallOrigin,
  confirmInvite, registry = ROOM_INVITE_REGISTRY, bridgeWindow = window, storage = window.localStorage }) {
  const [pending, setPending] = useState(null);
  const bridge = useRef(null), handled = useRef(""), prompted = useRef("");
  const loaded = getPersist("dataLoaded"), userInfo = getPersist("user_info");
  const current = useRef(null);
  current.current = { loaded, location, history, getPersist, confirmInvite };
  useEffect(() => {
    // Staging needs the static registry and durable storage, not a network-loaded account.
    // Navigation below still waits for the existing identity/persist hydration.
    const receiver = createRoomInviteBridge({ hallOrigin, storage, registry, onPending: setPending });
    bridge.current = receiver;
    const previous = bridgeWindow.RoomInviteBridge;
    const api = { version: 1, receive: envelope => receiver.receive(envelope), status: id => receiver.status(id) };
    bridgeWindow.RoomInviteBridge = api;
    setPending(receiver.pending());
    return () => {
      if (bridgeWindow.RoomInviteBridge === api) {
        if (previous) bridgeWindow.RoomInviteBridge = previous;
        else delete bridgeWindow.RoomInviteBridge;
      }
      if (bridge.current === receiver) bridge.current = null;
    };
  }, [hallOrigin, storage, registry, bridgeWindow]);

  useEffect(() => {
    if (!loaded) return;
    if (pending && !roomInviteHomePath(pending.homePath.slice("/home".length), registry)) {
      bridge.current.finish(pending.deliveryId);
      return;
    }
    const home = location.pathname === "/home" || location.pathname === "/";
    const canonicalRoutePath = roomInviteHomePath(location.search, registry);
    const routePath = roomInviteHomePath(location.search, registry, true);
    if (pending?.phase === "routing" && location.pathname === "/game" && pending.homePath === canonicalRoutePath) {
      bridge.current.finish(pending.deliveryId);
      return;
    }
    if (!home) {
      handled.current = "";
      if (!pending || (location.pathname === "/login" && getPersist("login_back_url") === pending.homePath) ||
        prompted.current === pending.deliveryId || typeof confirmInvite !== "function") return;
      prompted.current = pending.deliveryId;
      const id = pending.deliveryId, path = pending.homePath;
      Promise.resolve(confirmInvite()).then(accepted => {
        if (bridge.current?.pending()?.deliveryId !== id) return;
        if (accepted) current.current.history.replace(path);
        // "Later" keeps the durable intent for the next visit to the hall.
      }).catch(() => {});
      return;
    }
    const returnPath = pending?.homePath || routePath;
    if (!returnPath) { handled.current = ""; return; }
    const registered = isRoomInviteRegisteredPlayer(userInfo);
    const key = `${registered ? "game" : "register"}:${returnPath}`;
    if (handled.current === key) return;
    handled.current = key;
    const query = new URLSearchParams(returnPath.split("?")[1]);
    setPersist("agent_rid", query.get("aid") || "");
    setPersist("agent_from", query.get("f") || "");
    if (registered) {
      if (pending && !bridge.current.markRouted(pending.deliveryId)) { handled.current = ""; return; }
      history.replace(roomInviteGamePath(query, getGameUrl, hallOrigin, registry));
    } else {
      setPersist("isLogin", false);
      setPersist("login_back_url", returnPath);
      history.replace(roomInviteRegistrationPath(returnPath));
    }
  }, [loaded, location.pathname, location.search, userInfo, getGameUrl, hallOrigin, history, setPersist, getPersist,
    registry, pending, confirmInvite]);
}
